import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { AccountWideSettings } from './AccountWideSettings';
import { NotificationMatrix } from './NotificationMatrix';
import { NotificationCards } from './NotificationCards';
import { NotificationSettingsSkeleton } from './NotificationSettingsSkeleton';
import { ChannelControlStyle } from './ChannelControl';
import { NOTIFICATIONS_LEAD, NOTIFICATIONS_LEGEND } from './notificationStrings';
import {
  BORDER,
  CARD_BREAKPOINT_QUERY,
  CATEGORY_COLUMN_WIDTH,
  INK,
  MIN_CHANNEL_COLUMN_WIDTH,
  RADIUS,
  SHADOW_CARD,
  SURFACE,
} from './notificationTokens';
import {
  NotificationCategoryDefinitionType,
  NotificationChannelDefinitionType,
  NotificationRoleDefinitionType,
  NotificationTypeChannels,
  NotificationUniversalSettingDefinitionType,
  NotificationUniversalSettingType,
} from '../../util/NotificationUtil';

interface NotificationSettingsPanelProps {
  roles: NotificationRoleDefinitionType[];
  channels: NotificationChannelDefinitionType[];
  categories: NotificationCategoryDefinitionType[];
  settings: NotificationTypeChannels | undefined;
  onToggle: (channel: string, category: string, role: string, value: boolean) => void;

  changesMade: boolean;
  onSave: () => void;

  controlStyle?: ChannelControlStyle;

  /** Account-wide toggles (supplier only). Empty / omitted for buyers. */
  globalSettingDefinitions?: readonly NotificationUniversalSettingDefinitionType[];
  globalValues?: NotificationUniversalSettingType | null;
  onGlobalToggle?: (settingName: string, value: boolean) => void;
}

const NotificationSettingsPanel = ({
  roles,
  channels,
  categories,
  settings,
  onToggle,
  changesMade,
  onSave,
  controlStyle = 'chip',
  globalSettingDefinitions = [],
  globalValues,
  onGlobalToggle,
}: NotificationSettingsPanelProps) => {
  // Hard floor: always cards on phones.
  const belowFloor = useMediaQuery(CARD_BREAKPOINT_QUERY);

  // Switch to cards as soon as the matrix would no longer fit the available
  // width — i.e. before any horizontal scroll appears. We measure the real
  // content area (which already accounts for all ancestor + panel padding) and
  // compare it to the matrix's required width, which scales with the number of
  // role × channel columns (so buyers switch earlier than suppliers).
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState(0);

  // useLayoutEffect + a synchronous first measure means the very first paint
  // already reflects the real width — no matrix→cards flash — and the initial
  // value is correct even if ResizeObserver is unavailable (window resize is the
  // fallback there).
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => setContentWidth(el.clientWidth);
    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const matrixRequiredWidth = CATEGORY_COLUMN_WIDTH + roles.length * channels.length * MIN_CHANNEL_COLUMN_WIDTH;
  const isNarrow = belowFloor || (contentWidth > 0 && contentWidth < matrixRequiredWidth);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        backgroundColor: SURFACE.card,
        border: `1px solid ${BORDER.default}`,
        borderRadius: RADIUS.panel,
        boxShadow: SHADOW_CARD,
        p: { xs: 2.5, md: 4 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 3,
          flexWrap: 'wrap',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: INK.heading, mb: 0.5 }}>
            Notifications
          </Typography>
          <Typography sx={{ fontSize: 14, color: INK.secondary, maxWidth: '57ch', textWrap: 'pretty' }}>
            {NOTIFICATIONS_LEAD}
          </Typography>
        </Box>
        {/* Save button kept identical to the live page (button save mode). */}
        <Button variant={'contained'} color={'primary'} onClick={onSave} disabled={!changesMade}>
          Save
        </Button>
      </Box>

      {/* contentRef always wraps the body so its width is the available content
          area (used to decide the matrix↔cards swap) during loading, matrix, and
          card modes alike. The body renders only once the GET has resolved
          (settings defined), so no default-state flash on load. */}
      <Box ref={contentRef}>
        {settings ? (
          <>
            {onGlobalToggle && globalValues && (
              <AccountWideSettings
                settingDefinitions={globalSettingDefinitions}
                values={globalValues}
                onToggle={onGlobalToggle}
              />
            )}
            {isNarrow ? (
              <NotificationCards
                roles={roles}
                channels={channels}
                categories={categories}
                settings={settings}
                controlStyle={controlStyle}
                onToggle={onToggle}
              />
            ) : (
              <NotificationMatrix
                roles={roles}
                channels={channels}
                categories={categories}
                settings={settings}
                controlStyle={controlStyle}
                onToggle={onToggle}
              />
            )}
          </>
        ) : (
          <NotificationSettingsSkeleton
            roleCount={roles.length}
            channelCount={channels.length}
            categoryCount={categories.length}
          />
        )}
      </Box>

      <Box
        sx={{
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${BORDER.subtle}`,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 18,
            height: 18,
            borderRadius: RADIUS.pill,
            backgroundColor: '#334155',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <LockIcon sx={{ fontSize: 9 }} />
        </Box>
        <Typography sx={{ fontSize: 14, color: INK.secondary }}>{NOTIFICATIONS_LEGEND}</Typography>
      </Box>
    </Paper>
  );
};

export { NotificationSettingsPanel };
